(function (global) {
  'use strict';

  let PRESETS = [];
  let AR_LIST = [];
  let AR_TABLE = {};
  const bootedRoots = new WeakMap();

  function setEmbeddedConfig (config) {
    if (!config) return;
    PRESETS = config.PRESETS || [];
    AR_LIST = config.AR_LIST || [];
    AR_TABLE = config.AR_TABLE || {};
  }

  function resolveEmbedRoot () {
    return document.querySelector('[data-sac-strapi]');
  }

  function isSafeToBoot () {
    try {
      const path = String(global.location ? global.location.pathname : '').toLowerCase();
      if (path.indexOf('/admin') >= 0 || path.indexOf('content-manager') >= 0) return false;
      const embed = document.querySelector('[data-sac-strapi]');
      if (
        embed instanceof HTMLElement &&
        embed.closest('[contenteditable="true"], .ck-editor, .ck-editor__editable, .ck-content')
      ) {
        return false;
      }
    } catch (err) {
      return false;
    }
    return true;
  }

  function gcd (a, b) {
    return b === 0 ? a : gcd(b, a % b);
  }

  function safeBox (w, h, pct) {
    const mx = Math.round((w * (1 - pct)) / 2);
    const my = Math.round((h * (1 - pct)) / 2);
    return { sw: w - 2 * mx, sh: h - 2 * my, mx, my };
  }

  function windowbox (W, H, ar) {
    const containerAR = W / H;
    const key = `${W}x${H}`;
    const table = AR_TABLE[key];

    if (Math.abs(ar - containerAR) < 1e-4) {
      return { w: W, h: H, x: 0, y: 0, mode: 'match', exact: true };
    }
    if (ar < containerAR) {
      let w = table ? table[Object.keys(table).find((k) => Math.abs(k - ar) < 1e-4)] : null;
      const exact = w != null;
      if (!exact) w = Math.round(H * ar);
      const x = Math.round((W - w) / 2);
      return { w, h: H, x, y: 0, mode: 'pillar', exact };
    }
    let h = table ? table[Object.keys(table).find((k) => Math.abs(k - ar) < 1e-4)] : null;
    const exact = h != null;
    if (!exact) h = Math.round(W / ar);
    const y = Math.round((H - h) / 2);
    return { w: W, h, x: 0, y, mode: 'letter', exact };
  }

  function fmtBox (b) {
    return `${b.sw} × ${b.sh}`;
  }

  function findArRow (ar) {
    return AR_LIST.find((r) => Math.abs(r.ar - ar) < 1e-4);
  }

  function parseArText (text) {
    text = text.trim();
    if (!text) return null;
    const ratioMatch = text.match(/^([\d.]+)\s*[:/]\s*([\d.]+)$/);
    if (ratioMatch) {
      const num = parseFloat(ratioMatch[1]);
      const den = parseFloat(ratioMatch[2]);
      if (den > 0) return num / den;
      return null;
    }
    const v = parseFloat(text);
    return isFinite(v) && v > 0 ? v : null;
  }

  function initRoot (root) {
    if (!(root instanceof HTMLElement) || bootedRoots.has(root)) return false;

    const presetListEl = root.querySelector('#presetList');
    const wIn = root.querySelector('#wIn');
    const hIn = root.querySelector('#hIn');
    const arSelect = root.querySelector('#arSelect');
    const arCustom = root.querySelector('#arCustom');
    const arClear = root.querySelector('#arClear');

    if (!presetListEl || !wIn || !hIn || !arSelect || !arCustom || !arClear) return false;

    const state = { w: 1920, h: 1080, presetIndex: 1, ar: null };

    function renderPresetList () {
      presetListEl.innerHTML = '';
      PRESETS.forEach((p, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'preset-btn' + (state.presetIndex === i ? ' active' : '');
        btn.innerHTML = `<span>${p.name}</span><span class="res">${p.w}×${p.h}</span>`;
        btn.addEventListener('click', () => {
          state.presetIndex = i;
          state.w = p.w;
          state.h = p.h;
          wIn.value = p.w;
          hIn.value = p.h;
          update();
        });
        presetListEl.appendChild(btn);
      });
    }

    function renderArSelect () {
      arSelect.innerHTML = '';
      const blank = document.createElement('option');
      blank.value = '';
      blank.textContent = '— Full container (no framing) —';
      arSelect.appendChild(blank);
      const groups = {
        pillar: 'Pillarbox (narrower than 16:9)',
        letter: 'Letterbox (wider than 16:9)',
        portrait: 'Portrait / vertical'
      };
      ['pillar', 'letter', 'portrait'].forEach((mode) => {
        const og = document.createElement('optgroup');
        og.label = groups[mode];
        AR_LIST.filter((r) => r.mode === mode).forEach((r) => {
          const opt = document.createElement('option');
          opt.value = r.ar;
          opt.textContent = r.spec + (r.numden ? '  (' + r.numden + ')' : '');
          og.appendChild(opt);
        });
        arSelect.appendChild(og);
      });
    }

    function drawStage (W, H, pic) {
      const svg = root.querySelector('#svgStage');
      const pad = Math.max(W, H) * 0.045;
      svg.setAttribute('viewBox', `${-pad} ${-pad} ${W + pad * 2} ${H + pad * 2}`);
      const ns = 'http://www.w3.org/2000/svg';
      svg.innerHTML = '';
      const strokeW = Math.max(W, H) * 0.0035;

      const frame = document.createElementNS(ns, 'rect');
      frame.setAttribute('x', 0);
      frame.setAttribute('y', 0);
      frame.setAttribute('width', W);
      frame.setAttribute('height', H);
      frame.setAttribute('fill', 'var(--frame-bg)');
      frame.setAttribute('stroke', 'var(--frame-outline)');
      frame.setAttribute('stroke-width', strokeW);
      svg.appendChild(frame);

      const framing = pic.mode !== 'match';

      if (framing) {
        const bars = [];
        if (pic.mode === 'pillar') {
          bars.push([0, 0, pic.x, H]);
          bars.push([pic.x + pic.w, 0, W - (pic.x + pic.w), H]);
        } else if (pic.mode === 'letter') {
          bars.push([0, 0, W, pic.y]);
          bars.push([0, pic.y + pic.h, W, H - (pic.y + pic.h)]);
        }
        bars.forEach(([bx, by, bw, bh]) => {
          if (bw <= 0 || bh <= 0) return;
          const r = document.createElementNS(ns, 'rect');
          r.setAttribute('x', bx);
          r.setAttribute('y', by);
          r.setAttribute('width', bw);
          r.setAttribute('height', bh);
          r.setAttribute('fill', 'var(--frame-bar)');
          svg.appendChild(r);
        });

        const pictureRect = document.createElementNS(ns, 'rect');
        pictureRect.setAttribute('x', pic.x);
        pictureRect.setAttribute('y', pic.y);
        pictureRect.setAttribute('width', pic.w);
        pictureRect.setAttribute('height', pic.h);
        pictureRect.setAttribute('fill', 'none');
        pictureRect.setAttribute('stroke', 'var(--frame-picture)');
        pictureRect.setAttribute('stroke-width', strokeW * 0.9);
        pictureRect.setAttribute('stroke-dasharray', `${strokeW * 1.4} ${strokeW * 1.4}`);
        svg.appendChild(pictureRect);
      }

      const t = safeBox(pic.w, pic.h, 0.9);
      const a = safeBox(pic.w, pic.h, 0.93);

      const actionRect = document.createElementNS(ns, 'rect');
      actionRect.setAttribute('x', pic.x + a.mx);
      actionRect.setAttribute('y', pic.y + a.my);
      actionRect.setAttribute('width', a.sw);
      actionRect.setAttribute('height', a.sh);
      actionRect.setAttribute('fill', 'none');
      actionRect.setAttribute('stroke', 'var(--safe-action)');
      actionRect.setAttribute('stroke-width', strokeW * 1.15);
      actionRect.setAttribute('stroke-dasharray', `${strokeW * 5.5} ${strokeW * 3.2}`);
      svg.appendChild(actionRect);

      const titleRect = document.createElementNS(ns, 'rect');
      titleRect.setAttribute('x', pic.x + t.mx);
      titleRect.setAttribute('y', pic.y + t.my);
      titleRect.setAttribute('width', t.sw);
      titleRect.setAttribute('height', t.sh);
      titleRect.setAttribute('fill', 'none');
      titleRect.setAttribute('stroke', 'var(--safe-title)');
      titleRect.setAttribute('stroke-width', strokeW * 1.3);
      svg.appendChild(titleRect);

      return { t, a };
    }

    function update () {
      let W = state.w;
      let H = state.h;

      const effectiveAr = state.ar;
      const row = effectiveAr != null ? findArRow(effectiveAr) : null;
      if (row && row.mode === 'portrait') {
        W = state.h;
        H = state.w;
      }

      root.querySelector('#stageTitle').textContent = `${W} × ${H}`;
      const g = gcd(W, H);
      root.querySelector('#stageRatio').textContent = `ratio ${W / g}:${H / g}`;

      let pic;
      if (effectiveAr != null && !(row && row.mode === 'portrait')) {
        pic = windowbox(W, H, effectiveAr);
      } else {
        pic = { w: W, h: H, x: 0, y: 0, mode: 'match', exact: true };
      }

      const { t, a } = drawStage(W, H, pic);

      const framing = pic.mode !== 'match';
      root.querySelector('#pictureCard').hidden = !framing;
      root.querySelector('#arFormulaLine').hidden = !framing;

      if (framing) {
        root.querySelector('#p_mode_label').textContent =
          pic.mode === 'pillar' ? '— pillarboxed' : '— letterboxed';
        const srcEl = root.querySelector('#p_source');
        srcEl.textContent = pic.exact ? 'chart value' : 'computed';
        srcEl.className = 'pill ' + (pic.exact ? 'pill-ok' : 'pill-warn');
        root.querySelector('#p_box').textContent = fmtBox({ sw: pic.w, sh: pic.h });
        if (pic.mode === 'pillar') {
          root.querySelector('#p_bar_label').textContent = 'Pillarbox L/R';
          root.querySelector('#p_bars').textContent = pic.x + ' px each side';
        } else {
          root.querySelector('#p_bar_label').textContent = 'Letterbox T/B';
          root.querySelector('#p_bars').textContent = pic.y + ' px each side';
        }
        root.querySelector('#p_origin').textContent = `(${pic.x}, ${pic.y})`;
        const notesEl = root.querySelector('#p_notes');
        if (row && row.notes) {
          notesEl.hidden = false;
          notesEl.textContent = row.notes;
        } else {
          notesEl.hidden = true;
        }
      }

      root.querySelector('#a_box').textContent = fmtBox(a);
      root.querySelector('#a_mx').textContent = a.mx + ' px';
      root.querySelector('#a_my').textContent = a.my + ' px';
      root.querySelector('#a_origin_label').textContent = framing
        ? 'Origin in container (x,y)'
        : 'Origin (x,y)';
      root.querySelector('#a_origin').textContent = `(${pic.x + a.mx}, ${pic.y + a.my})`;

      root.querySelector('#t_box').textContent = fmtBox(t);
      root.querySelector('#t_mx').textContent = t.mx + ' px';
      root.querySelector('#t_my').textContent = t.my + ' px';
      root.querySelector('#t_origin_label').textContent = framing
        ? 'Origin in container (x,y)'
        : 'Origin (x,y)';
      root.querySelector('#t_origin').textContent = `(${pic.x + t.mx}, ${pic.y + t.my})`;

      const arNote = root.querySelector('#arNote');
      if (effectiveAr != null) {
        arNote.hidden = false;
        arClear.hidden = false;
        const arLabel = effectiveAr.toFixed(5).replace(/0+$/, '').replace(/\.$/, '');
        arNote.innerHTML =
          `Framing at AR <b>${arLabel}</b>` +
          (row ? ` &mdash; ${row.spec}` : ' (custom)') +
          `<span class="src">Source: ${row ? 'AR_samples chart' : 'typed value'}</span>`;
      } else {
        arNote.hidden = true;
        arClear.hidden = true;
      }

      const ratioNote = root.querySelector('#ratioNote');
      if (ratioNote) {
        const rg = gcd(state.w, state.h);
        ratioNote.textContent = `Aspect ratio ${state.w / rg}:${state.h / rg}`;
      }

      renderPresetList();
    }

    function onCustomInput () {
      const w = Math.max(16, Math.min(16384, parseInt(wIn.value, 10) || 0));
      const h = Math.max(16, Math.min(16384, parseInt(hIn.value, 10) || 0));
      state.w = w;
      state.h = h;
      state.presetIndex = PRESETS.findIndex((p) => p.w === w && p.h === h);
      update();
    }

    function onArSelectChange () {
      if (arSelect.value === '') {
        state.ar = null;
      } else {
        state.ar = parseFloat(arSelect.value);
        arCustom.value = '';
      }
      update();
    }

    function onArCustomInput () {
      const v = parseArText(arCustom.value);
      if (v != null) {
        state.ar = v;
        arSelect.value = '';
        update();
      } else if (arCustom.value.trim() === '') {
        state.ar = null;
        update();
      }
    }

    function onArClearClick () {
      state.ar = null;
      arSelect.value = '';
      arCustom.value = '';
      update();
    }

    wIn.addEventListener('input', onCustomInput);
    hIn.addEventListener('input', onCustomInput);
    arSelect.addEventListener('change', onArSelectChange);
    arCustom.addEventListener('input', onArCustomInput);
    arClear.addEventListener('click', onArClearClick);

    renderArSelect();
    update();

    bootedRoots.set(root, () => {
      wIn.removeEventListener('input', onCustomInput);
      hIn.removeEventListener('input', onCustomInput);
      arSelect.removeEventListener('change', onArSelectChange);
      arCustom.removeEventListener('input', onArCustomInput);
      arClear.removeEventListener('click', onArClearClick);
      bootedRoots.delete(root);
    });

    return true;
  }

  function autoInitEmbeddedUi () {
    if (!isSafeToBoot()) return;
    const root = resolveEmbedRoot();
    initRoot(root);
  }

  function resetEmbeddedUi () {
    const root = resolveEmbedRoot();
    const cleanup = bootedRoots.get(root);
    if (cleanup) cleanup();
  }

  const api = {
    setEmbeddedConfig,
    autoInitEmbeddedUi,
    resetEmbeddedUi
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.SafeAreaCalculator = api;
})(typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : this);
